import { useMemo } from "react";
import dayjs from "dayjs";
import {
  useGetMissionControlTenantProductMixQuery,
  ProductMixGroup,
  ProductMixMonth,
} from "src/store/api/generatedApi";

export const formatMixPercentage = (value: number) =>
  `${Math.round(value * 100)}%`;

const aggregateProductMix = (
  allMonths: ProductMixMonth[][]
): ProductMixMonth[] => {
  const monthGroupMass = new Map<string, Map<string, number>>();

  allMonths.flat().forEach((monthData) => {
    const month = monthData.month_start;
    const groupMassByName =
      monthGroupMass.get(month) ?? new Map<string, number>();

    monthData.groups.forEach((group) => {
      groupMassByName.set(
        group.mix_group_name,
        (groupMassByName.get(group.mix_group_name) ?? 0) +
          group.produced_mass_tonnes
      );
    });

    monthGroupMass.set(month, groupMassByName);
  });

  return [...monthGroupMass.entries()]
    .sort(([monthA], [monthB]) => monthB.localeCompare(monthA))
    .map(([month_start, groupsByName]) => {
      const totalMass = [...groupsByName.values()].reduce(
        (sum, mass) => sum + mass,
        0
      );
      const groups: ProductMixGroup[] = [...groupsByName.entries()]
        .map(([mix_group_name, produced_mass_tonnes]) => ({
          mix_group_name,
          produced_mass_tonnes,
          mix_percentage: totalMass > 0 ? produced_mass_tonnes / totalMass : 0,
        }))
        .sort((a, b) => a.mix_group_name.localeCompare(b.mix_group_name));

      return {
        month_start,
        groups,
      };
    });
};

const aggregateMixGroupNames = (allNames: string[][]): string[] => {
  const orderedUniqueNames: string[] = [];
  allNames.flat().forEach((name) => {
    if (!orderedUniqueNames.includes(name)) {
      orderedUniqueNames.push(name);
    }
  });
  return orderedUniqueNames;
};

export const useTenantProductMix = (tenantNames: string[]) => {
  const primaryTenant = tenantNames[0] ?? "";
  const secondaryTenant = tenantNames[1] ?? "";

  const { data: primaryProductMixData, isLoading: primaryLoading } =
    useGetMissionControlTenantProductMixQuery(
      { tenantName: primaryTenant },
      { skip: primaryTenant.length === 0 }
    );
  const { data: secondaryProductMixData, isLoading: secondaryLoading } =
    useGetMissionControlTenantProductMixQuery(
      { tenantName: secondaryTenant },
      { skip: secondaryTenant.length === 0 }
    );

  const isLoading = primaryLoading || secondaryLoading;

  const hasProductMixData =
    (primaryProductMixData?.months.length ?? 0) > 0 ||
    (secondaryProductMixData?.months.length ?? 0) > 0;

  const mixGroupNames: string[] = useMemo(() => {
    const allGroupNames = [
      primaryProductMixData?.mix_group_names ?? [],
      secondaryProductMixData?.mix_group_names ?? [],
    ];
    return aggregateMixGroupNames(allGroupNames);
  }, [primaryProductMixData, secondaryProductMixData]);

  const months: ProductMixMonth[] = useMemo(() => {
    if (!hasProductMixData) return [];
    return aggregateProductMix([
      primaryProductMixData?.months ?? [],
      secondaryProductMixData?.months ?? [],
    ]);
  }, [hasProductMixData, primaryProductMixData, secondaryProductMixData]);

  const latestMonth = useMemo(() => {
    if (months.length === 0) return null;
    return months[0];
  }, [months]);

  const formattedMonth = useMemo(() => {
    if (!latestMonth) return "";
    return dayjs(latestMonth.month_start).format("MMM YYYY");
  }, [latestMonth]);

  return {
    isLoading,
    hasProductMixData,
    mixGroupNames,
    months,
    latestMonth,
    formattedMonth,
  };
};
